/**
 * 1.8.5 §5/§6 — "Add these steps to my summary": the Tech Wizard sends a window event (`abf:wizard-steps` with
 * `{ appointmentId, text }`). The close-out sheet may not be open yet when it is pressed, so the steps are kept here,
 * per ticket, in memory only (never the browser's storage), until the sheet takes them into its summary box.
 */
import { WIZARD_STEPS_EVENT, type WizardStepsDetail } from '@shared/tech-visit';

const pending = new Map<string, string[]>();
const listeners = new Map<string, (text: string) => void>();
let installed = false;

function onSteps(e: Event) {
  const d = (e as CustomEvent<WizardStepsDetail>).detail;
  if (!d || typeof d.appointmentId !== 'string' || typeof d.text !== 'string' || !d.text.trim()) return;
  const open = listeners.get(d.appointmentId);
  if (open) return open(d.text.trim());
  pending.set(d.appointmentId, [...(pending.get(d.appointmentId) ?? []), d.text.trim()]);
}

/** Listen once for the whole app (the ticket page and Today call it; it is harmless to call again). */
export function installWizardSteps(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener(WIZARD_STEPS_EVENT, onSteps);
}

/** The steps sent for a ticket while no sheet was listening — taken once. */
export function takePendingSteps(appointmentId: string): string[] {
  const out = pending.get(appointmentId) ?? [];
  pending.delete(appointmentId);
  return out;
}

/** An open sheet takes every new step for its ticket as it is sent. Returns the unsubscribe. */
export function onWizardSteps(appointmentId: string, fn: (text: string) => void): () => void {
  installWizardSteps();
  listeners.set(appointmentId, fn);
  return () => {
    if (listeners.get(appointmentId) === fn) listeners.delete(appointmentId);
  };
}

/** The steps a summary box already has, plus new ones, a blank line between. */
export function appendSteps(current: string, steps: string): string {
  const c = current.replace(/\s+$/, '');
  return c ? `${c}\n\n${steps.trim()}` : steps.trim();
}
